import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Send, Sparkles, Trash2, User } from "lucide-react";
import { api, API } from "@/lib/api";
import { getToken } from "@/lib/tokenStorage";
import { toast } from "sonner";

export default function Chat() {
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const scrollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    const r = await api.get("/chat/sessions");
    setSessions(r.data);
    setActiveId((cur) => cur || r.data[0]?.id || null);
  }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    if (!activeId) { setMessages([]); return; }
    api.get(`/chat/sessions/${activeId}/messages`).then((r) => setMessages(r.data));
  }, [activeId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streamingText]);

  const newSession = async () => {
    const r = await api.post("/chat/sessions", { title: "Nuova conversazione" });
    await loadSessions();
    setActiveId(r.data.id);
    setMessages([]);
  };

  const deleteSession = async (id) => {
    await api.delete(`/chat/sessions/${id}`);
    if (activeId === id) setActiveId(null);
    loadSessions();
    toast.success("Conversazione eliminata");
  };

  const send = async () => {
    if (!input.trim() || streaming) return;
    let sid = activeId;
    if (!sid) {
      const r = await api.post("/chat/sessions", { title: input.slice(0, 60) });
      sid = r.data.id;
      setActiveId(sid);
      await loadSessions();
    }
    const userMsg = { id: `tmp-${Date.now()}`, role: "user", content: input, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    const question = input;
    setInput("");
    setStreaming(true);
    setStreamingText("");
    try {
      const token = getToken();
      const resp = await fetch(`${API}/chat/sessions/${sid}/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: question }),
      });
      if (!resp.ok) throw new Error("stream error");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          acc += data.replace(/\\n/g, "\n");
          setStreamingText(acc);
        }
      }
      // reload from server for authoritative messages
      const r = await api.get(`/chat/sessions/${sid}/messages`);
      setMessages(r.data);
      setStreamingText("");
      loadSessions();
    } catch (e) {
      toast.error("Errore risposta AI");
    } finally {
      setStreaming(false);
    }
  };

  const suggestions = [
    "Come funziona il regime forfettario?",
    "Quando scade il 730 quest'anno?",
    "Come attivare lo SPID?",
    "Devo pagare il bollo se l'auto è in fermo amministrativo?",
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-6 h-[calc(100vh-5rem)]" data-testid="chat-page">
      {/* Sessions sidebar */}
      <Card className="p-4 rounded-2xl border flex flex-col overflow-hidden">
        <Button onClick={newSession} className="rounded-full mb-4" data-testid="chat-new-session"><Plus className="w-4 h-4 mr-2" /> Nuova chat</Button>
        <ScrollArea className="flex-1 -mr-2 pr-2">
          <div className="space-y-1">
            {sessions.map((s) => (
              <div key={s.id} className={`group flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer transition-colors ${activeId === s.id ? "bg-accent" : "hover:bg-accent/50"}`} onClick={() => setActiveId(s.id)} data-testid={`chat-session-${s.id}`}>
                <div className="flex-1 min-w-0 text-sm truncate">{s.title}</div>
                <button onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }} className="opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`chat-del-${s.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {sessions.length === 0 && <div className="text-xs text-muted-foreground p-2">Nessuna conversazione</div>}
          </div>
        </ScrollArea>
      </Card>

      {/* Chat area */}
      <Card className="p-0 rounded-2xl border flex flex-col overflow-hidden">
        <div className="p-4 border-b flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center"><Sparkles className="w-5 h-5 text-secondary" /></div>
          <div>
            <div className="font-heading font-semibold">TUOTAX AI</div>
            <div className="text-xs text-muted-foreground">Il tuo copilota fiscale</div>
          </div>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="chat-messages">
          {messages.length === 0 && !streamingText && (
            <div className="max-w-lg mx-auto text-center py-12">
              <div className="w-14 h-14 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-4"><Sparkles className="w-7 h-7 text-primary" /></div>
              <h2 className="font-heading font-bold text-2xl mb-2">Come posso aiutarti?</h2>
              <p className="text-muted-foreground text-sm mb-6">Chiedi qualsiasi cosa su fisco, tasse e burocrazia italiana.</p>
              <div className="grid gap-2">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => setInput(s)} className="p-3 rounded-xl border text-sm text-left hover:bg-accent transition-colors" data-testid={`chat-suggestion-${s.slice(0,10)}`}>{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => (
            <div key={m.id} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0"><Sparkles className="w-4 h-4 text-secondary" /></div>}
              <div className={`max-w-[75%] rounded-2xl p-4 ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-accent"}`}>
                <div className="prose-chat text-sm whitespace-pre-wrap">{m.content}</div>
              </div>
              {m.role === "user" && <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0"><User className="w-4 h-4 text-primary-foreground" /></div>}
            </div>
          ))}
          {streaming && (
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0"><Sparkles className="w-4 h-4 text-secondary" /></div>
              <div className="max-w-[75%] rounded-2xl p-4 bg-accent">
                <div className="prose-chat text-sm whitespace-pre-wrap" data-testid="chat-streaming">{streamingText || "..."}</div>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t">
          <div className="flex gap-2">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Scrivi la tua domanda… (Invio per inviare)"
              className="rounded-xl min-h-[52px] max-h-40 resize-none"
              data-testid="chat-input"
              disabled={streaming}
            />
            <Button onClick={send} disabled={!input.trim() || streaming} className="rounded-full h-[52px] w-[52px] p-0 flex-shrink-0" data-testid="chat-send">
              <Send className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
